import {
  Color,
  HalfFloatType,
  LinearFilter,
  NoBlending,
  RGBAFormat,
  ShaderMaterial,
  Vector2,
  WebGLRenderTarget,
} from 'three';
import { Pass, FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

const LEVELS = 4;
const BLACK = new Color(0x000000);

const vertexShader = /* glsl */`
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

function target(width, height, name) {
  const rt = new WebGLRenderTarget(width, height, {
    type: HalfFloatType,
    format: RGBAFormat,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
    depthBuffer: false,
    stencilBuffer: false,
  });
  rt.texture.name = name;
  rt.texture.generateMipmaps = false;
  return rt;
}

function material(uniforms, fragmentShader) {
  return new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    blending: NoBlending,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
}

/**
 * Half-resolution HDR bloom using a four-level dual-Kawase pyramid.
 *
 * The public fields intentionally mirror UnrealBloomPass. In particular,
 * callers can continue to animate `strength` without knowing which bloom
 * implementation owns the composer slot.
 */
class KawaseBloomPass extends Pass {
  constructor(resolution, strength = 1, radius = 0, threshold = 1) {
    super();

    this.strength = strength;
    this.radius = radius;
    this.threshold = threshold;
    this.resolution = resolution !== undefined
      ? new Vector2(resolution.x, resolution.y)
      : new Vector2(256, 256);

    this._brightTarget = target(1, 1, 'KawaseBloomPass.bright');
    this._downTargets = [];
    this._upTargets = [];
    for (let i = 0; i < LEVELS; i++) {
      this._downTargets.push(target(1, 1, `KawaseBloomPass.down${i}`));
      this._upTargets.push(target(1, 1, `KawaseBloomPass.up${i}`));
    }

    this._brightMaterial = material({
      tDiffuse: { value: null },
      threshold: { value: threshold },
      // An absolute HDR knee keeps the selected pixels close to Unreal's
      // threshold=2.2 set while removing the hard edge at the cutoff.
      knee: { value: 0.35 },
    }, /* glsl */`
      varying vec2 vUv;
      uniform sampler2D tDiffuse;
      uniform float threshold;
      uniform float knee;

      void main() {
        vec4 source = texture2D(tDiffuse, vUv);
        float brightness = dot(source.rgb, vec3(0.2126, 0.7152, 0.0722));
        float contribution = smoothstep(threshold, threshold + knee, brightness);
        gl_FragColor = vec4(source.rgb * contribution, contribution);
      }
    `);

    this._downMaterial = material({
      tDiffuse: { value: null },
      texelSize: { value: new Vector2() },
      offset: { value: 1 },
    }, /* glsl */`
      varying vec2 vUv;
      uniform sampler2D tDiffuse;
      uniform vec2 texelSize;
      uniform float offset;

      void main() {
        vec2 o = texelSize * offset;
        vec3 a = texture2D(tDiffuse, vUv + vec2(-2.0,  2.0) * o).rgb;
        vec3 b = texture2D(tDiffuse, vUv + vec2( 0.0,  2.0) * o).rgb;
        vec3 c = texture2D(tDiffuse, vUv + vec2( 2.0,  2.0) * o).rgb;
        vec3 d = texture2D(tDiffuse, vUv + vec2(-1.0,  1.0) * o).rgb;
        vec3 e = texture2D(tDiffuse, vUv + vec2( 1.0,  1.0) * o).rgb;
        vec3 f = texture2D(tDiffuse, vUv + vec2(-2.0,  0.0) * o).rgb;
        vec3 g = texture2D(tDiffuse, vUv).rgb;
        vec3 h = texture2D(tDiffuse, vUv + vec2( 2.0,  0.0) * o).rgb;
        vec3 i = texture2D(tDiffuse, vUv + vec2(-1.0, -1.0) * o).rgb;
        vec3 j = texture2D(tDiffuse, vUv + vec2( 1.0, -1.0) * o).rgb;
        vec3 k = texture2D(tDiffuse, vUv + vec2(-2.0, -2.0) * o).rgb;
        vec3 l = texture2D(tDiffuse, vUv + vec2( 0.0, -2.0) * o).rgb;
        vec3 m = texture2D(tDiffuse, vUv + vec2( 2.0, -2.0) * o).rgb;

        vec3 color = (d + e + i + j) * 0.125;
        color += (a + b + f + g) * 0.03125;
        color += (b + c + g + h) * 0.03125;
        color += (f + g + k + l) * 0.03125;
        color += (g + h + l + m) * 0.03125;
        gl_FragColor = vec4(color, 1.0);
      }
    `);

    this._upMaterial = material({
      tLow: { value: null },
      tBase: { value: null },
      texelSize: { value: new Vector2() },
      offset: { value: 1 },
    }, /* glsl */`
      varying vec2 vUv;
      uniform sampler2D tLow;
      uniform sampler2D tBase;
      uniform vec2 texelSize;
      uniform float offset;

      void main() {
        vec2 o = texelSize * offset;
        vec3 blur = texture2D(tLow, vUv + vec2(-1.0, -1.0) * o).rgb;
        blur += texture2D(tLow, vUv + vec2( 0.0, -1.0) * o).rgb * 2.0;
        blur += texture2D(tLow, vUv + vec2( 1.0, -1.0) * o).rgb;
        blur += texture2D(tLow, vUv + vec2(-1.0,  0.0) * o).rgb * 2.0;
        blur += texture2D(tLow, vUv).rgb * 4.0;
        blur += texture2D(tLow, vUv + vec2( 1.0,  0.0) * o).rgb * 2.0;
        blur += texture2D(tLow, vUv + vec2(-1.0,  1.0) * o).rgb;
        blur += texture2D(tLow, vUv + vec2( 0.0,  1.0) * o).rgb * 2.0;
        blur += texture2D(tLow, vUv + vec2( 1.0,  1.0) * o).rgb;
        gl_FragColor = vec4(texture2D(tBase, vUv).rgb + blur * 0.0625, 1.0);
      }
    `);

    this._compositeMaterial = material({
      tDiffuse: { value: null },
      tBloom: { value: null },
      strength: { value: strength },
    }, /* glsl */`
      varying vec2 vUv;
      uniform sampler2D tDiffuse;
      uniform sampler2D tBloom;
      uniform float strength;

      void main() {
        vec4 source = texture2D(tDiffuse, vUv);
        // The up pyramid contains five energy-preserving levels. 1.8 gives
        // the same nominal summed energy as Unreal's weighted, 3x composite.
        vec3 bloom = texture2D(tBloom, vUv).rgb * strength * 1.8;
        gl_FragColor = vec4(source.rgb + bloom, source.a);
      }
    `);

    this._fsQuad = new FullScreenQuad(null);
    this._oldClearColor = new Color();
    this._oldClearAlpha = 1;
    this._width = 0;
    this._height = 0;
    this.setSize(this.resolution.x, this.resolution.y);
  }

  setSize(width, height) {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    if (w === this._width && h === this._height) return;

    this._width = w;
    this._height = h;
    this.resolution.set(w, h);

    let levelW = Math.max(1, Math.round(w / 2));
    let levelH = Math.max(1, Math.round(h / 2));
    this._brightTarget.setSize(levelW, levelH);

    for (let i = 0; i < LEVELS; i++) {
      levelW = Math.max(1, Math.round(levelW / 2));
      levelH = Math.max(1, Math.round(levelH / 2));
      this._downTargets[i].setSize(levelW, levelH);
    }

    // up0 is half resolution; up3 is the first step back from the smallest mip.
    levelW = Math.max(1, Math.round(w / 2));
    levelH = Math.max(1, Math.round(h / 2));
    for (let i = 0; i < LEVELS; i++) {
      this._upTargets[i].setSize(levelW, levelH);
      levelW = Math.max(1, Math.round(levelW / 2));
      levelH = Math.max(1, Math.round(levelH / 2));
    }
  }

  render(renderer, writeBuffer, readBuffer, deltaTime, maskActive) {
    renderer.getClearColor(this._oldClearColor);
    this._oldClearAlpha = renderer.getClearAlpha();
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(BLACK, 0);

    if (maskActive) renderer.state.buffers.stencil.setTest(false);

    this._brightMaterial.uniforms.tDiffuse.value = readBuffer.texture;
    this._brightMaterial.uniforms.threshold.value = this.threshold;
    this._fsQuad.material = this._brightMaterial;
    renderer.setRenderTarget(this._brightTarget);
    renderer.clear();
    this._fsQuad.render(renderer);

    // A/B against UnrealBloom at radius 0.42: 1.5 left the battle-card halo ~30% tighter
    // (30 vs 43 CSS px); 2.4 widens the pyramid's per-level step so the spread matches.
    const sampleOffset = 0.85 + Math.max(0, this.radius) * 2.4;
    let input = this._brightTarget;
    this._fsQuad.material = this._downMaterial;
    this._downMaterial.uniforms.offset.value = sampleOffset;
    for (let i = 0; i < LEVELS; i++) {
      this._downMaterial.uniforms.tDiffuse.value = input.texture;
      this._downMaterial.uniforms.texelSize.value.set(1 / input.width, 1 / input.height);
      renderer.setRenderTarget(this._downTargets[i]);
      renderer.clear();
      this._fsQuad.render(renderer);
      input = this._downTargets[i];
    }

    this._fsQuad.material = this._upMaterial;
    this._upMaterial.uniforms.offset.value = sampleOffset;
    for (let i = LEVELS - 1; i >= 0; i--) {
      const base = i === 0 ? this._brightTarget : this._downTargets[i - 1];
      this._upMaterial.uniforms.tLow.value = input.texture;
      this._upMaterial.uniforms.tBase.value = base.texture;
      this._upMaterial.uniforms.texelSize.value.set(1 / input.width, 1 / input.height);
      renderer.setRenderTarget(this._upTargets[i]);
      renderer.clear();
      this._fsQuad.render(renderer);
      input = this._upTargets[i];
    }

    if (maskActive) renderer.state.buffers.stencil.setTest(true);

    this._compositeMaterial.uniforms.tDiffuse.value = readBuffer.texture;
    this._compositeMaterial.uniforms.tBloom.value = input.texture;
    this._compositeMaterial.uniforms.strength.value = this.strength;
    this._fsQuad.material = this._compositeMaterial;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this._fsQuad.render(renderer);

    renderer.setClearColor(this._oldClearColor, this._oldClearAlpha);
    renderer.autoClear = oldAutoClear;
  }

  dispose() {
    this._brightTarget.dispose();
    for (const rt of this._downTargets) rt.dispose();
    for (const rt of this._upTargets) rt.dispose();
    this._brightMaterial.dispose();
    this._downMaterial.dispose();
    this._upMaterial.dispose();
    this._compositeMaterial.dispose();
    this._fsQuad.dispose();
  }
}

export { KawaseBloomPass };
